import './dashboardApplicationPage.scss';

import { EmbeddedInsurance } from '@ei-tech/embedded-component';
import { ThemeProvider } from '@mui/material/styles';
import { ChecklightSessionStatus } from '@thesavingsgroup/enums/ChecklightSessionStatus';
import { isAutopayNumber } from '@thesavingsgroup/utils/isAutopayNumber';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import VehicleImagePlaceholder from '../../assets/vehicle-placeholder.png';
import { Alert } from '../../components/Alert/Alert';
import { ApplicationProgressModal } from '../../components/ApplicationProgressModal/ApplicationProgressModal';
import { ApplicationQueuedLearnModal } from '../../components/ApplicationQueuedLearnModal/ApplicationQueuedLearnModal';
import { ApplicationUpdatedModal } from '../../components/ApplicationUpdatedModal/ApplicationUpdatedModal';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { NavigationPane } from '../../components/NavigationPane/NavigationPane';
import { RefandiBanner } from '../../components/RefandiBanner/RefandiBanner';
import { Stepper } from '../../components/Stepper/Stepper';
import { resetStore as defaultResetStore } from '../../store/_core/resetStore';
import useAnalyticStore from '../../store/analyticStore/useAnalyticStore';
import useApplicantStore from '../../store/applicantStore/useApplicantStore';
import { HydrateSource } from '../../store/applyStore/enums/HydrateSource';
import useApplyStore from '../../store/applyStore/useApplyStore';
import useCoapplicantStore from '../../store/coapplicantStore/useCoapplicantStore';
import { useWatchApplicationStatus } from '../../store/dashboardStore/hooks/useWatchApplicationStatus';
import useDashboardStore from '../../store/dashboardStore/useDashboardStore';
import { FeatureFlag } from '../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../store/featureStore/hooks/useFeatureFlag';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import useRouteStore from '../../store/routeStore/useRouteStore';
import useTaskStore from '../../store/taskStore/useTaskStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useThirdPartyStore from '../../store/thirdPartyStore/useThirdPartyStore';
import { locator } from '../../testing/e2e-locator/locator';
import { DashboardApplicationPageProps } from './types/DashboardApplicationPageProps';

export function Component({
  resetStore = defaultResetStore,
}: DashboardApplicationPageProps): ReactNode {
  const { vehicleId: paramVehicleId } = useParams();
  const navigate = useNavigate();

  const { createTheme } = useThemeStore();
  const theme = createTheme();

  const { hydrate } = useApplyStore();

  const { leadChannelCode, toggleLeadChannelCode } = useParameterStore();
  const { assignLocation } = useRouteStore();
  const {
    currentResidenceState,
    retryFailedSubmissionFailedMessage,
    retryFailedSubmissionIsLoading,
    loadApplicant,
    retryFailedSubmission,
  } = useApplicantStore();
  const {
    sendApplicationTaskButtonClickedAnalytics,
    sendApplicationViewedAnalytics,
    sendNewApplicationAnalytics,
  } = useAnalyticStore();
  const {
    isEmbeddedInsuranceLayoutVisible,
    getEmbeddedInsuranceContent,
    getEmbeddedInsuranceURLWithBrand,
  } = useThirdPartyStore();

  const { t } = useTranslation();

  const [showApplicationProgressModal, setShowApplicationProgressModal] =
    useState(
      // if the param vehicleId is an autopay number, show the modal
      // otherwise, don't show the modal
      isAutopayNumber(paramVehicleId),
    );

  const [showApplicationUpdatedModal, setShowApplicationUpdatedModal] =
    useState(false);

  const [showApplicationQueuedLearnModal, setShowApplicationQueuedLearnModal] =
    useState(false);

  const {
    computed: {
      isVehicleEmpty,
      vehicleApplicationAutopayNumberTrimmed,
      vehicleApplicationEmployeeEmail,
      vehicleApplicationEmployeeName,
      vehicleApplicationEmployeePhone,
      vehicleApplicationId,
      vehicleApplicationInSubmission,
      vehicleApplicationIsRefinanceType,
      vehicleApplicationIsQueuedForSubmission,
      vehicleApplicationProgress,
      vehicleApplicationProgressPreview,
      vehicleApplicationSourceStatus,
      vehicleApplicationStatus,
      vehicleApplicationTask,
      vehicleBrand,
      vehicleDefaultImage,
      vehicleEmbeddedInsuranceLeadId,
      vehicleId,
    },
    currentVehicle,
    fetchVehicleByApplicationAutopayNumberFailedMessage,
    fetchVehicleByIdFailedMessage,
    isFetchingVehicleById,
    loadCurrentVehicle,
    fetchVehicleApplicationById,
  } = useDashboardStore();

  const {
    isApplicantTaskStatus,
    isCanceledTaskStatus,
    isFailedTaskStatus,
    isHandOffTaskStatus,
    isQueuedTaskStatus,
  } = useTaskStore();

  const vehicleApplicationIsCanceledTask = isCanceledTaskStatus(
    vehicleApplicationSourceStatus,
  );

  const vehicleApplicationIsApplicantTask = isApplicantTaskStatus(
    vehicleApplicationSourceStatus,
  );

  const vehicleApplicationIsQueuedTask = isQueuedTaskStatus(
    vehicleApplicationStatus,
  );

  const vehicleApplicationIsFailedTask = isFailedTaskStatus(
    vehicleApplicationStatus,
  );

  const vehicleApplicationIsHandOffTask = isHandOffTaskStatus(
    vehicleApplicationSourceStatus,
  );

  const refandiComponentEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_LOAN_DOCUMENTS_MODULE,
  );

  const embeddedInsuranceComponentEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_EMBEDDED_INSURANCE_COMPONENT,
    {
      residenceState: currentResidenceState,
    },
  );

  const enableOriginChannelAttribution = useFeatureFlag(
    FeatureFlag.ENABLE_ORIGIN_CHANNEL_ATTRIBUTION_FOR_NEW_APPS,
    { leadChannelCode },
  );

  const showRefandiBanner = [
    refandiComponentEnabled,
    vehicleApplicationIsRefinanceType,
    !vehicleApplicationInSubmission,
  ].every(Boolean);

  const renderEmbeddedInsuranceBanner = [
    vehicleEmbeddedInsuranceLeadId,
    embeddedInsuranceComponentEnabled,
  ].every(Boolean);

  const showEmbeddedInsuranceBanner = [
    vehicleEmbeddedInsuranceLeadId,
    embeddedInsuranceComponentEnabled,
    isEmbeddedInsuranceLayoutVisible,
  ].every(Boolean);

  const showBannerContent = [
    showRefandiBanner,
    renderEmbeddedInsuranceBanner,
  ].some(Boolean);

  const showBannerHeader = [
    showRefandiBanner,
    showEmbeddedInsuranceBanner,
  ].some(Boolean);

  const applicationUpdated = useWatchApplicationStatus({
    enabled: vehicleApplicationIsQueuedForSubmission,
    statusToLeave: ChecklightSessionStatus.QUEUED_FOR_SUBMISSION,
    fetchLatestApplication: () =>
      fetchVehicleApplicationById(vehicleId, vehicleApplicationId),
  });

  const backClicked = (): void => {
    navigate('/dashboard/garage');
  };

  const viewAllClicked = (): void => {
    setShowApplicationProgressModal(true);
  };

  const closeApplicationProgressModal = (): void => {
    setShowApplicationProgressModal(false);
  };

  const closeApplicationQueuedLearnModal = (): void => {
    setShowApplicationQueuedLearnModal(false);
  };

  const taskClicked = async (): Promise<void> => {
    sendApplicationTaskButtonClickedAnalytics(
      vehicleApplicationStatus,
      vehicleApplicationTask.actionTitle,
    );

    if (vehicleApplicationIsHandOffTask) {
      assignLocation(`tel:${vehicleApplicationEmployeePhone}`);
      return;
    }

    if (vehicleApplicationIsQueuedTask) {
      setShowApplicationQueuedLearnModal(true);
      return;
    }

    if (vehicleApplicationIsFailedTask) {
      await retryFailedSubmission(vehicleId);
    }

    if (vehicleApplicationIsCanceledTask) {
      sendNewApplicationAnalytics();

      toggleLeadChannelCode(enableOriginChannelAttribution);

      await hydrate({
        vehicle: currentVehicle,
        source: HydrateSource.CANCELED_APPLICATION,
      });
    }

    if (vehicleApplicationIsApplicantTask) {
      // Reset applicant and coapplicant stores to ensure a clean state for the applicant workflow.
      resetStore(useApplicantStore);
      resetStore(useCoapplicantStore);
    }

    navigate(vehicleApplicationTask.destination);
  };

  useEffect((): void => {
    loadCurrentVehicle(paramVehicleId);
  }, []);

  useEffect((): void => {
    const shouldRedirectToNotFound = [
      fetchVehicleByIdFailedMessage,
      fetchVehicleByApplicationAutopayNumberFailedMessage,
    ].some(isNonEmptyString);

    if (shouldRedirectToNotFound) {
      navigate('/not-found');
    }
  }, [
    fetchVehicleByIdFailedMessage,
    fetchVehicleByApplicationAutopayNumberFailedMessage,
  ]);

  useEffect((): void => {
    if (isVehicleEmpty) {
      return;
    }

    // Send analytics for application viewed
    sendApplicationViewedAnalytics(vehicleApplicationStatus);

    // Load applicant data for use in feature flag context
    loadApplicant();
  }, [isVehicleEmpty]);

  useEffect((): void => {
    if (applicationUpdated) {
      setShowApplicationUpdatedModal(true);
    }
  }, [applicationUpdated]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page dashboard-application-page">
        <Header
          showDrawerToggle
          showDrawerToggleOnMobileOnly
          onBackClick={backClicked}
        />

        <main data-testid={locator.pages.dashboardApplication.main.root}>
          <NavigationPane />

          <div className="col">
            {isFetchingVehicleById && (
              <div>
                <div className="content empty">
                  <span className="skeleton-loading-sweep" />
                  <span className="skeleton-loading-sweep" />
                </div>
                <div className="content empty">
                  <span className="skeleton-loading-sweep" />
                  <span className="skeleton-loading-sweep" />
                </div>
              </div>
            )}

            {!isFetchingVehicleById &&
              !isEmpty(retryFailedSubmissionFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.dashboardApplication.alert
                        .retryFailedSubmission
                    }
                    severity="error"
                  >
                    {retryFailedSubmissionFailedMessage}
                  </Alert>
                </div>
              )}

            {!isFetchingVehicleById && currentVehicle && (
              <div
                className="content"
                data-testid={
                  locator.pages.dashboardApplication.main.taskSectionRoot
                }
              >
                <span>
                  <h1
                    data-testid={
                      locator.pages.dashboardApplication.main.taskSectionH1
                    }
                  >
                    {vehicleBrand}
                    {vehicleApplicationAutopayNumberTrimmed && <div>|</div>}
                    {vehicleApplicationAutopayNumberTrimmed &&
                      `#${vehicleApplicationAutopayNumberTrimmed}`}
                  </h1>
                  <h2
                    data-testid={
                      locator.pages.dashboardApplication.main.taskSectionH2
                    }
                  >
                    {vehicleApplicationTask.title}
                  </h2>
                  <p
                    data-testid={
                      locator.pages.dashboardApplication.main
                        .taskSectionDescription
                    }
                  >
                    {vehicleApplicationTask.description}
                  </p>
                  <Button
                    data-testid={
                      locator.pages.dashboardApplication.main
                        .taskSectionActionButton
                    }
                    disabled={
                      vehicleApplicationTask.inactive ||
                      retryFailedSubmissionIsLoading
                    }
                    loader={retryFailedSubmissionIsLoading}
                    fullWidth
                    onClick={taskClicked}
                  >
                    {vehicleApplicationTask.actionTitle}
                  </Button>
                </span>

                <span>
                  <img
                    alt={t('imageAlts.applicationVehicle')}
                    data-testid={
                      locator.pages.dashboardApplication.main.vehicleImage
                    }
                    src={vehicleDefaultImage || VehicleImagePlaceholder}
                  />
                </span>
              </div>
            )}

            {!isFetchingVehicleById && currentVehicle && (
              <div
                className="content fill-rounded"
                data-testid={
                  locator.pages.dashboardApplication.main.progressSectionRoot
                }
                style={{ marginTop: '3rem' }}
              >
                <span>
                  <div className="head">
                    <h3
                      data-testid={
                        locator.pages.dashboardApplication.main
                          .progressSectionH3
                      }
                    >
                      {t('DashboardApplicationPage.progress')}
                    </h3>
                    {!vehicleApplicationIsCanceledTask && (
                      <button
                        data-testid={
                          locator.pages.dashboardApplication.main
                            .progressSectionViewAllButton
                        }
                        type="button"
                        onClick={viewAllClicked}
                      >
                        {t('DashboardApplicationPage.viewAll')}
                      </button>
                    )}
                  </div>

                  {(vehicleApplicationIsCanceledTask ||
                    isEmpty(vehicleApplicationProgressPreview)) && (
                    <label
                      className="empty-head"
                      data-testid={
                        locator.pages.dashboardApplication.main
                          .progressSectionEmptyText
                      }
                    >
                      {t('DashboardApplicationPage.noInformationAvailable')}
                    </label>
                  )}

                  {!vehicleApplicationIsCanceledTask &&
                    !isEmpty(vehicleApplicationProgressPreview) && (
                      <Stepper
                        steps={vehicleApplicationProgressPreview}
                        testId={
                          locator.pages.dashboardApplication.main
                            .progressSectionStepper
                        }
                        titleTestId={
                          locator.pages.dashboardApplication.main
                            .getProgressSectionStepTitle
                        }
                      />
                    )}
                </span>

                <span
                  data-testid={
                    locator.pages.dashboardApplication.main.contactSectionRoot
                  }
                >
                  <div className="head">
                    <h4
                      data-testid={
                        locator.pages.dashboardApplication.main.contactSectionH4
                      }
                    >
                      {t('DashboardApplicationPage.contact')}
                    </h4>
                  </div>

                  <ul>
                    <li>
                      <dl>
                        <dt>{t('DashboardApplicationPage.name')}</dt>
                        <dd
                          data-testid={
                            locator.pages.dashboardApplication.main
                              .contactSectionEmployeeName
                          }
                        >
                          {vehicleApplicationEmployeeName}
                        </dd>
                      </dl>
                    </li>
                    <li>
                      <dl>
                        <dt>{t('DashboardApplicationPage.phone')}</dt>
                        <dd
                          data-testid={
                            locator.pages.dashboardApplication.main
                              .contactSectionEmployeePhone
                          }
                        >
                          {vehicleApplicationEmployeePhone}
                        </dd>
                      </dl>
                    </li>
                    <li>
                      <dl>
                        <dt>{t('DashboardApplicationPage.email')}</dt>
                        <dd
                          data-testid={
                            locator.pages.dashboardApplication.main
                              .contactSectionEmployeeEmail
                          }
                        >
                          {vehicleApplicationEmployeeEmail}
                        </dd>
                      </dl>
                    </li>
                  </ul>
                </span>
              </div>
            )}

            {showBannerContent && (
              <div
                className="banner-content"
                data-testid={
                  locator.pages.dashboardApplication.main.bannerSectionRoot
                }
              >
                {showBannerHeader && (
                  <h5>{t('DashboardApplicationPage.otherWaysToSave')}</h5>
                )}

                {renderEmbeddedInsuranceBanner && (
                  <EmbeddedInsurance
                    getContent={getEmbeddedInsuranceContent}
                    getUrl={getEmbeddedInsuranceURLWithBrand(vehicleBrand)}
                    leadId={vehicleEmbeddedInsuranceLeadId}
                    pollingInterval={20000}
                  />
                )}

                {showRefandiBanner && <RefandiBanner />}
              </div>
            )}

            <footer>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </div>
        </main>
      </div>

      {showApplicationProgressModal && !isEmpty(currentVehicle) && (
        <ApplicationProgressModal
          progressSteps={vehicleApplicationProgress}
          onClose={closeApplicationProgressModal}
        />
      )}

      {showApplicationUpdatedModal && <ApplicationUpdatedModal />}

      {showApplicationQueuedLearnModal && (
        <ApplicationQueuedLearnModal
          onClose={closeApplicationQueuedLearnModal}
        />
      )}

      {/* Mobile Drawer */}
      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'DashboardApplicationPage';
